import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCrown, 
  FaAward,
  FaPaintBrush, 
  FaChartLine, 
  FaUsers, 
  FaMousePointer,
  FaLink,
  FaSpinner
} from 'react-icons/fa';
import { loadConfig } from '../utils/config';

const PricingCard = ({ title, price, features, recommended, onSelect, loading }) => (
  <div className={`card bg-base-100 shadow-xl ${recommended ? 'border-2 border-primary' : ''}`}>
    <div className="card-body">
      {recommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="badge badge-primary">Recommended</span>
        </div>
      )}
      <h2 className="card-title text-2xl justify-center mb-2">{title}</h2>
      <div className="text-center mb-6">
        <span className="text-4xl font-bold">${price}</span>
        <span className="text-base-content/60">/month</span>
      </div>
      <ul className="space-y-4 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-3">
            {React.createElement(feature.icon, { className: `text-${feature.color} text-lg` })}
            <span>{feature.text}</span>
          </li>
        ))}
      </ul>
      <div className="card-actions justify-center mt-auto">
        <button 
          className={`btn ${recommended ? 'btn-primary' : 'btn-outline'} btn-wide ${loading ? 'loading' : ''}`}
          onClick={onSelect}
          disabled={loading}
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Processing...
            </>
          ) : (
            'Select Plan'
          )}
        </button>
      </div>
    </div>
  </div>
);

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const config = await loadConfig();
        const response = await fetch(`${config.api.baseUrl}functions/v1/user-subscriptions/current`, {
          credentials: 'include',
          headers: {
            ...config.api.cors.headers,
            'Origin': config.api.cors.origin
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setCurrentSubscription(data);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
    };

    fetchSubscription();
  }, []);

  const basicFeatures = [
    { icon: FaLink, text: "Remove Promo Link", color: "primary" },
    { icon: FaAward, text: "Add Custom Badges", color: "success" },
    { icon: FaPaintBrush, text: "Additional Themes", color: "accent" }
  ];

  const proFeatures = [
    ...basicFeatures,
    { icon: FaChartLine, text: "Link Analytics Dashboard", color: "info" },
    { icon: FaMousePointer, text: "Click Tracking", color: "warning" },
    { icon: FaUsers, text: "Viewer Demographics", color: "error" }
  ];

  const handleSelectPlan = async (plan) => {
    try {
      setLoading(true);
      setError(null);
      
      const config = await loadConfig();
      const response = await fetch(`${config.api.baseUrl}functions/v1/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...config.api.cors.headers,
          'Content-Type': 'application/json',
          'Origin': config.api.cors.origin
        },
        body: JSON.stringify({
          priceId: plan
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to process subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async (action) => {
    try {
      setLoading(true);
      setError(null);
      
      const config = await loadConfig();
      const response = await fetch(`${config.api.baseUrl}functions/v1/manage-subscription`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...config.api.cors.headers,
          'Content-Type': 'application/json',
          'Origin': config.api.cors.origin
        },
        body: JSON.stringify({
          action,
          subscriptionId: currentSubscription.stripe_subscription_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to manage subscription');
      }

      // Refresh subscription status
      const updatedSubscription = await response.json();
      setCurrentSubscription(updatedSubscription);
    } catch (err) {
      console.error('Error managing subscription:', err);
      setError('Failed to update subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base-200">
      {/* Navigation Bar */}
      <div className="navbar bg-base-100">
        <div className="flex-1">
          <button onClick={() => navigate('/dashboard')} className="btn btn-ghost text-xl">XStreamLink</button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="alert alert-error mb-8">
            <span>{error}</span>
          </div>
        )}

        {currentSubscription && (
          <div className="card bg-base-100 shadow-xl mb-8">
            <div className="card-body">
              <h2 className="card-title">Current Subscription</h2>
              <p>Status: <span className="capitalize">{currentSubscription.status}</span></p>
              {currentSubscription.cancel_at_period_end && (
                <p className="text-warning">Your subscription will end at the current period end.</p>
              )}
              <div className="card-actions justify-end">
                {currentSubscription.cancel_at_period_end ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleManageSubscription('reactivate')}
                    disabled={loading}
                  >
                    Reactivate Subscription
                  </button>
                ) : (
                  <button 
                    className="btn btn-warning"
                    onClick={() => handleManageSubscription('cancel')}
                    disabled={loading}
                  >
                    Cancel at Period End
                  </button>
                )}
                <button 
                  className="btn btn-error"
                  onClick={() => handleManageSubscription('cancel_immediately')}
                  disabled={loading}
                >
                  Cancel Immediately
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">
            <FaCrown className="text-yellow-400 mr-3" />
            {currentSubscription ? 'Change Your Plan' : 'Upgrade Your Stream'}
          </h1>
          <p className="text-xl text-base-content/70">
            Choose the perfect plan to enhance your streaming experience
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <PricingCard
            title="Basic"
            price="4.95"
            features={basicFeatures}
            recommended={false}
            onSelect={() => handleSelectPlan('basic')}
            loading={loading}
          />
          <PricingCard
            title="Pro"
            price="9.95"
            features={proFeatures}
            recommended={true}
            onSelect={() => handleSelectPlan('pro')}
            loading={loading}
          />
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="collapse collapse-plus bg-base-100">
              <input type="radio" name="faq-accordion" defaultChecked /> 
              <div className="collapse-title text-xl font-medium">
                How do I get started?
              </div>
              <div className="collapse-content">
                <p>Simply select your preferred plan and follow the checkout process. Once completed, your account will be instantly upgraded with the new features.</p>
              </div>
            </div>
            <div className="collapse collapse-plus bg-base-100">
              <input type="radio" name="faq-accordion" />
              <div className="collapse-title text-xl font-medium">
                Can I switch plans later?
              </div>
              <div className="collapse-content">
                <p>Yes! You can upgrade or downgrade your plan at any time. The price difference will be prorated based on your billing cycle.</p>
              </div>
            </div>
            <div className="collapse collapse-plus bg-base-100">
              <input type="radio" name="faq-accordion" />
              <div className="collapse-title text-xl font-medium">
                What payment methods do you accept?
              </div>
              <div className="collapse-content">
                <p>We accept all major credit cards, PayPal, and popular payment methods. Your payment information is securely processed and encrypted.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage; 